import { DownOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { Checkbox, Col, Form, Input, message, Modal, Row, Switch } from "antd";
import { Rule } from "antd/lib/form";
import TextArea from "antd/lib/input/TextArea";
import { roleApi } from "api/role.api";
import {
  allPermissionItems,
  PERMISSION_ACTIONS,
  PERMISSION_SECTIONS,
  permissionKey,
  permissionMenu,
  PermissionAction,
  PermissionFeature,
  PermissionGroup,
} from "config/permissionMenu";
import React, { useImperativeHandle, useMemo, useState } from "react";
import { permissionStore } from "store/permissionStore";
import { userStore } from "store/userStore";
import { ModalStatus } from "types/modal";
import { Permission, Role } from "types/role";

const rules: Rule[] = [{ required: true, message: "Bắt buộc" }];

export interface RoleModal {
  handleCreate: () => void;
  handleUpdate: (Role: Role) => void;
}
interface RoleModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

const ALL_KEYS = allPermissionItems().map((e) => permissionKey(e.path, e.action));

// 5 cột quyền hay dùng; các quyền còn lại gom vào cột "Quyền đặc biệt"
const CORE_ACTIONS = PERMISSION_ACTIONS.filter((e) => ["VIEW", "CREATE", "UPDATE", "DELETE", "ONLY_MINE"].includes(e.key));
const CORE_KEYS = new Set<PermissionAction>(CORE_ACTIONS.map((e) => e.key));
const SPECIAL_ACTIONS = PERMISSION_ACTIONS.filter((e) => !CORE_KEYS.has(e.key));

type Row = { group: PermissionGroup; feature: PermissionFeature; label: string };

const rowLabel = (feature: PermissionFeature) => feature.title.replace(/\s*\(Menu\)\s*$/, "");
// Bỏ dấu để tìm "khach hang" vẫn ra "Khách hàng"
const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .toLowerCase();

const ALL_ROWS: Row[] = permissionMenu.flatMap((group) =>
  group.features.map((feature) => ({ group, feature, label: rowLabel(feature) })),
);

export const RoleModal = React.forwardRef(({ onClose, onSubmitOk }: RoleModalProps, ref) => {
  const [form] = Form.useForm<Role>();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<ModalStatus>("create");
  const [role, setRole] = useState<Role>();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [originPermissions, setOriginPermissions] = useState<Permission[]>([]);
  const [search, setSearch] = useState("");
  const [onlyGranted, setOnlyGranted] = useState(false);
  const [closedSections, setClosedSections] = useState<Set<string>>(new Set());

  const isAllChecked = ALL_KEYS.length > 0 && ALL_KEYS.every((key) => checked.has(key));

  const resetView = () => {
    setSearch("");
    setOnlyGranted(false);
    setClosedSections(new Set());
  };

  const grantedOf = (rows: Row[]) =>
    rows.reduce((sum, row) => sum + row.feature.actions.filter((action) => checked.has(permissionKey(row.feature.path, action))).length, 0);
  const totalOf = (rows: Row[]) => rows.reduce((sum, row) => sum + row.feature.actions.length, 0);

  // Các nhóm kèm dòng menu còn lại sau khi lọc (tìm kiếm, chỉ hiện menu đã cấp quyền)
  const sections = useMemo(() => {
    const keyword = normalize(search.trim());
    return PERMISSION_SECTIONS.map((section) => {
      const all = ALL_ROWS.filter((row) => row.group.section === section);
      const rows = all.filter((row) => {
        if (keyword && !normalize(`${row.label} ${row.group.title}`).includes(keyword)) return false;
        if (onlyGranted && !row.feature.actions.some((action) => checked.has(permissionKey(row.feature.path, action)))) return false;
        return true;
      });
      return { section, all, rows };
    }).filter((item) => item.rows.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, onlyGranted, checked]);

  const fetchOriginPermissions = async () => {
    const res = await roleApi.getPermission();
    setOriginPermissions(res.data);
  };

  useImperativeHandle<any, RoleModal>(
    ref,
    () => ({
      handleCreate() {
        form.resetFields();
        setChecked(new Set());
        resetView();
        setVisible(true);
        setStatus("create");
        setRole(undefined);
        fetchOriginPermissions();
      },
      async handleUpdate(Role: Role) {
        const detailRole = await roleApi.findOne(Role.id);

        form.setFieldsValue({ ...detailRole.data });
        setChecked(
          new Set(
            detailRole.data.permissions.map((e: Permission) => permissionKey(e.path, e.action || "VIEW")),
          ),
        );
        resetView();
        setVisible(true);
        setStatus("update");
        setRole(Role);
        fetchOriginPermissions();
      },
    }),
    [],
  );

  const toggleAll = (value: boolean) => {
    setChecked(new Set(value ? ALL_KEYS : []));
  };

  const toggleAction = (path: string, action: PermissionAction, value: boolean) => {
    const next = new Set(checked);
    const key = permissionKey(path, action);

    if (value) {
      next.add(key);
    } else {
      next.delete(key);
      // Bỏ quyền Xem thì bỏ luôn các quyền còn lại của chức năng
      if (action === "VIEW") {
        for (const item of PERMISSION_ACTIONS) {
          next.delete(permissionKey(path, item.key));
        }
      }
    }
    setChecked(next);
  };

  const toggleSection = (section: string) => {
    setClosedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const handleClose = () => {
    form.resetFields();
    setChecked(new Set());
    setVisible(false);
    onClose();
  };

  const handleSubmit = async () => {
    await form.validateFields();

    const permissionIds = Array.from(checked)
      .map((key) => {
        const [path, action] = key.split("|");
        return originPermissions.find((p) => p.path === path && (p.action || "VIEW") === action)?.id;
      })
      .filter(Boolean);

    if (checked.size && permissionIds.length < checked.size) {
      message.warning('Một số quyền chưa có trong hệ thống, hãy bấm "Import routes" rồi thử lại');
    }

    const data = { role: form.getFieldsValue(), permissionIds };

    setLoading(true);
    try {
      if (status === "create") {
        await roleApi.create(data);
        message.success("Tạo mới thành công");
      } else {
        await roleApi.update(role?.id || 0, data);
        message.success("Cập nhật thành công");
        if (role?.id && role.id === userStore.info.role?.id && !userStore.info.role?.isAdmin) {
          await permissionStore.fetchPermissions(role.id);
        }
      }
      handleClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const searching = !!search.trim();

  return (
    <Modal
      onCancel={handleClose}
      open={visible}
      title={status === "create" ? "Tạo mới vai trò" : "Cập nhật vai trò"}
      style={{ top: 20 }}
      width={1100}
      okText="Đồng ý"
      cancelText="Hủy"
      confirmLoading={loading}
      onOk={handleSubmit}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Tên vai trò" name="name" rules={rules} style={{ marginBottom: 12 }}>
              <Input placeholder="" />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item label="Mô tả vai trò" name="description" rules={rules} style={{ marginBottom: 12 }}>
              <TextArea rows={1} autoSize={{ minRows: 1, maxRows: 2 }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Thanh công cụ: tìm menu, chỉ hiện menu đã cấp quyền, toàn quyền, bộ đếm */}
      <div className="flex flex-wrap items-center gap-4 mb-2">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Tìm menu…"
          style={{ width: 240 }}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Checkbox checked={onlyGranted} onChange={(event) => setOnlyGranted(event.target.checked)}>
          Chỉ hiện menu đã cấp quyền
        </Checkbox>
        <div className="flex items-center gap-2">
          <Switch checked={isAllChecked} onChange={toggleAll} />
          <span>Toàn quyền</span>
        </div>
        <span className="ml-auto text-sm text-gray-500">
          Đã cấp {checked.size}/{ALL_KEYS.length} quyền
        </span>
      </div>

      {/* Ma trận: mỗi dòng là một menu, nhóm theo thanh menu; tiêu đề bảng cố định khi cuộn */}
      <div className="border border-gray-200 rounded-lg overflow-auto" style={{ maxHeight: "calc(100vh - 360px)", minHeight: 260 }}>
        <table className="w-full text-sm" style={{ tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr>
              <th className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-3 py-2 text-left font-semibold" style={{ width: 230 }}>
                Menu
              </th>
              {CORE_ACTIONS.map((column) => (
                <th key={column.key} className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-1 py-2 text-center font-semibold" style={{ width: 84 }}>
                  {column.title}
                </th>
              ))}
              <th className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-3 py-2 text-left font-semibold">Quyền đặc biệt</th>
            </tr>
          </thead>
          <tbody>
            {sections.length === 0 && (
              <tr>
                <td colSpan={CORE_ACTIONS.length + 2} className="px-3 py-6 text-center text-gray-400">
                  Không có menu phù hợp
                </td>
              </tr>
            )}
            {sections.map(({ section, all, rows }) => {
              const closed = closedSections.has(section) && !searching;
              return (
                <React.Fragment key={section}>
                  <tr className="cursor-pointer select-none" onClick={() => toggleSection(section)}>
                    <td colSpan={CORE_ACTIONS.length + 2} className="bg-gray-100 border-b border-gray-200 px-3 py-2 font-semibold">
                      {closed ? <RightOutlined className="mr-2" /> : <DownOutlined className="mr-2" />}
                      {section}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {grantedOf(all)}/{totalOf(all)} quyền
                      </span>
                    </td>
                  </tr>
                  {!closed &&
                    rows.map(({ feature, label }) => {
                      const canView = checked.has(permissionKey(feature.path, "VIEW"));
                      return (
                        <tr key={feature.path} className="hover:bg-gray-50">
                          <td className="border-b border-gray-100 px-3 py-2">{label}</td>
                          {CORE_ACTIONS.map((column) => (
                            <td key={column.key} className="border-b border-gray-100 px-1 py-2 text-center">
                              {feature.actions.includes(column.key) && (
                                <Checkbox
                                  checked={checked.has(permissionKey(feature.path, column.key))}
                                  disabled={column.key !== "VIEW" && !canView}
                                  onChange={(e) => toggleAction(feature.path, column.key, e.target.checked)}
                                />
                              )}
                            </td>
                          ))}
                          <td className="border-b border-gray-100 px-3 py-2">
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {SPECIAL_ACTIONS.filter((column) => feature.actions.includes(column.key)).map((column) => (
                                <Checkbox
                                  key={column.key}
                                  checked={checked.has(permissionKey(feature.path, column.key))}
                                  disabled={!canView}
                                  onChange={(e) => toggleAction(feature.path, column.key, e.target.checked)}
                                >
                                  {column.title}
                                </Checkbox>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
});
